import jsPDF from 'jspdf';
import moment from 'moment';
import 'moment-timezone';
import { formatLocalDateAndTime, getTimezone } from '../support/helpers/format';
import autoTable from 'jspdf-autotable';
import { IChecklistItem } from '../checklists/model';
import { IOperator } from '../profile/IOperator';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import { Feature, Polygon } from '@turf/turf';
import { Theme } from '../app/ThemeVariables';
import { MissionColorProvider } from '../flight-checklist/MissionColorProvider';
import html2canvas from 'html2canvas';
import { EditableMissionState } from '../mission/model/EditableMissionState';
import { LAANCAuthorizationTextProvider } from '../mission/laanc-authorization/LAANCAuthorizationTextProvider';
import './MissionReport.css';
import { LatLngFormatPreferenceProvider } from '../map/LatLngFormatPreferenceProvider';

type ISO8601 = string;

export interface IMissionReportParams {
    generatedAt: ISO8601;
    operator: IOperator;
    mission: EditableMissionState;
}

const INCHES_TO_POINTS = 72;

interface ITextParams {
    text: string;
    xInches?: number;
    yInches?: number;
align?: 'right';
    maxInches?: number;
    color?: string;
}
export class MissionReport {
    static inject = () => [
        Theme,
        MissionColorProvider,
        LAANCAuthorizationTextProvider,
        LatLngFormatPreferenceProvider
    ];
    constructor(
        private theme: Theme,
        private missionColorProvider: MissionColorProvider,
        private authorizationTextProvider: LAANCAuthorizationTextProvider,
        private latLngFormatPreferenceProvider: LatLngFormatPreferenceProvider
    ){}
    private doc!: jsPDF;

    private text = ({ text, xInches, yInches, align, maxInches, color  }: ITextParams) => {
        if (maxInches){
            const result = this.doc.splitTextToSize(text, maxInches * INCHES_TO_POINTS);
            text = result[0];
        }

        xInches = xInches || this.cursor.x;
        yInches = yInches || this.cursor.y;

        this.doc.setTextColor(color || 'black');

        this.doc.text(text, xInches * INCHES_TO_POINTS, yInches * INCHES_TO_POINTS, {
            align: align || 'left'
        });
    };

    private margins = {
        left: .5,
        right: .5,
        top: .5
    };

    private pageSize = {
        height: 11,
        width: 8.5
    };

    private cursor = {
        x: 0,
        y: 0
    };

    private h1Points = 14;
    private h1 = (params: ITextParams) => {
        this.doc.setFontSize(this.h1Points)
            .setFont(undefined as unknown as string, 'bold');
        this.text(params);
        this.cursor.y = this.cursor.y + (this.h1Points / INCHES_TO_POINTS) + .1;
    };

    private h2Points = 12;
    private h2 =  (params: ITextParams) => {
        this.doc.setFontSize(this.h2Points)
            .setFont(undefined as unknown as string, 'bold');
        this.text(params);
        this.cursor.y = this.cursor.y + (this.h2Points / INCHES_TO_POINTS) + .05;
    };

    private pPoints = 10;
    private p =  (params: ITextParams) => {
        this.doc.setFontSize(this.pPoints)
            .setFont(undefined as unknown as string, 'normal');
        this.text(params);
        this.cursor.y = this.cursor.y + (this.pPoints / INCHES_TO_POINTS) + .025;
    };

    private p2Points = 8;
    private p2 =  (params: ITextParams) => {
        this.doc.setFontSize(this.p2Points)
            .setFont(undefined as unknown as string, 'normal');
        this.text(params);
        this.cursor.y = this.cursor.y + (this.p2Points / INCHES_TO_POINTS) + .025;
    };

    private hr = ({ yInches, color }: { yInches: number; color?: string; }) => {
        this.doc.setLineWidth(1);

        this.doc.setDrawColor(color || 'black');

        this.doc.line(this.margins.left * INCHES_TO_POINTS, yInches * INCHES_TO_POINTS, (this.pageSize.width - this.margins.right) * INCHES_TO_POINTS, yInches * INCHES_TO_POINTS, 'S');
    };
    private addHeader = ({  mission: mission2, generatedAt,  }: IMissionReportParams) => {
        const pageCount = this.doc.getNumberOfPages();
        for (let page = 1; page <= pageCount; page++){
            this.doc.setPage(page);

            this.h1({
                text: 'Operational Snapshot',
                xInches: this.pageSize.width - this.margins.right,
                yInches: this.margins.top,
                align: 'right'
            });
            const address = mission2.getAddress() || {
                street: '',
                city: '',
                state: '',
                zip: ''
            };

            const fullAddress = `${address.street || ''} ${address.city} ${address.state} ${address.zip || ''}`;
            const logoWidth = 2;
            this.p({
                text: fullAddress,
                maxInches: this.pageSize.width - this.margins.right - this.margins.left - logoWidth - 1,
                xInches: this.pageSize.width - this.margins.right,
                yInches: .72,
                align: 'right'
            });

            const generatedLine = `Generated on ${moment.tz(generatedAt, moment.tz.guess()).format('M/D/YY')} at ${moment.tz(generatedAt, moment.tz.guess()).format('h:mm A zz')}`;
            this.text({
                text: generatedLine,
                xInches: this.pageSize.width - this.margins.right,
                yInches: .9,
                align: 'right'
            });

            this.hr({ yInches: 1.0 });

            // 1024 × 209 px
            const widthInches = logoWidth;
            const aspectRatio = 209 / 1024;
            const heightInches = widthInches * aspectRatio;

            this.doc.addImage(
                '/assets/images/logo-name-horiz.png',
                'PNG',
                this.margins.right * INCHES_TO_POINTS,
                .35 * INCHES_TO_POINTS, widthInches * INCHES_TO_POINTS,
                heightInches * INCHES_TO_POINTS,
                undefined,
                this.imageCompression
            );
        }

    };

    private indentWidth = .1;

    private addMissionDetails = (params: IMissionReportParams) => {
        this.cursor.y = this.cursor.y + .3;
        this.h1({ text: 'Mission Details' });

        const row1Y = this.cursor.y;
        const col1X = this.margins.left + this.indentWidth;
        const col2X = this.pageSize.width / 2;
        const maxInches = this.pageSize.width / 2 - col1X;

        this.cursor.x = col1X;
        this.cursor.y = row1Y;
        this.h2({ text: 'Operation Name'});
        this.p({ text: params.mission.getName(), maxInches });

        const row2Y = this.cursor.y + this.rowMargin;

        this.cursor.x = col2X;
        this.cursor.y = row1Y;
        const center = params.mission.getLocation();

        const missionTZ  = getTimezone(center);

        this.h2({ text: 'Flight Day' });
        const startTime = params.mission.getStartTime();
        const flightDay = `${moment.tz(startTime, missionTZ).format('dddd MMMM D, yyyy')}`;
        this.p({ text: flightDay });

        this.cursor.x = col1X;
        this.cursor.y = row2Y;
        this.h2({ text: 'Flight Location' });
        const address = params.mission.getAddress() || {
            street: '',
            city: '',
            state: '',
            zip: ''
        };

        this.p({ text: address.street || '', maxInches });
        this.p({ text: `${address.city}, ${address.state} ${address.zip || ''}`, maxInches });

        const row3Y = this.cursor.y + this.rowMargin;

        this.cursor.x = col2X;
        this.cursor.y = row2Y;

        this.h2({ text: 'Flight Time'});
        const endTime = params.mission.getEndTime();
        const flightTime = `${moment.tz(startTime, missionTZ).format('h:mm A')} - ${moment.tz(endTime, missionTZ).format('h:mm A zz')}`;
        this.p({ text: flightTime });
        const durationMinutes = Math.round(moment.duration(moment(endTime).diff(moment(startTime))).asMinutes());
        this.p({ text: `${durationMinutes} minutes` });


        this.cursor.x = col1X;
        this.cursor.y = row3Y;

        this.h2({ text: 'Lat / Lng' });
        const location = params.mission.getLocation()!;
        this.p({
            text: this.latLngFormatPreferenceProvider.getFormattedLatLng(location)
            // replace special characters not currently supported by our pdf font
                .replaceAll('′', `'`)
                .replaceAll('″', `"`)
        });

        const row4Y = this.cursor.y + this.rowMargin;

        this.cursor.x = col2X;
        this.cursor.y = row3Y;

        this.h2({ text: 'Altitude' });
        this.p({ text: `${params.mission.getMaxAltitude()} Feet AGL` });

        this.cursor.x = col1X;
        this.cursor.y = row4Y;

        const relevantAuthorizations = params.mission.getRelevantAuthorizations();

        const laancReferenceCodes = relevantAuthorizations.map(authorization => this.authorizationTextProvider.getAuthorizationSummary({
            authorization,
            endTime
        }));

        const s = laancReferenceCodes.length > 1 ? 's' : '';
        this.h2({ text: `LAANC Reference Code${s}` });
        if (laancReferenceCodes.length === 0){
            this.p({ text: 'Not Applicable' });
        }
        else {
            for (let i = 0; i < laancReferenceCodes.length; i++){
                this.p({ text: laancReferenceCodes[i] });
            }
        }

        let endY = this.cursor.y;

        this.cursor.x = col2X;
        this.cursor.y = row4Y;

        this.h2({ text: 'Airspace' });

        const authorizations = params.mission.getMissionRadius().getLAANCAuthorizeableAdvisoryFeatures().map(feature => feature.properties);

        const airspaces = Array.from(new Set(authorizations.map(authorization => {
            const { airspaceIdent, airspaceClasses } = authorization;
            return `${airspaceIdent} | ${airspaceClasses || [].join('/')}`;
        })));

        if (airspaces.length === 0){
            this.p({ text: 'Class G' });
        }
        else {
            for (let i = 0; i < airspaces.length; i++){
                const [ ident, airClass ] = airspaces[i].split('|');
                const y = this.cursor.y;
                this.p({ text: ident });

                // reset cursor to append | ${airclass}
                this.cursor.y = y;
                this.cursor.x = col2X + .4;
                this.p({ text: `| ${airClass}` });

                // put x back where it should be
                this.cursor.x = col2X;
            }
        }

        if (this.cursor.y > endY){
            endY = this.cursor.y;
        }
        this.cursor.x = this.margins.left;

        this.cursor.y = endY + .3;

    };

    private addFooter = () => {
        const pageCount = this.doc.getNumberOfPages();
        for (let page = 1; page <= pageCount; page++){
            this.doc.setPage(page);

            const color = 'gray';
            this.hr({ yInches: 10, color });
            this.p2({ text: 'AutoPylot, INC', xInches: this.margins.left, yInches: 10.2, color });
            this.p2({ text: 'www.autopylot.io', xInches: this.margins.left, yInches: 10.32, color });
            this.p2({ text: `Page ${page} of ${pageCount}`, xInches: this.pageSize.width - this.margins.right, yInches: 10.32, align: 'right', color });
        }

    };

    private addChecklistItems = ({ checklist, startY }: { checklist: IChecklistItem[]; startY: number; }) => {
        while (checklist.length < 5){
            checklist.push({ name: '', id: 'blank', completed: false });
        }

        autoTable(this.doc, {
            didDrawCell: (data) => {
                const rowIndex = data.row.index;

                // rowIndex -1 indicates a continuation line when a checklist item wraps on a page boundary...
                // ideally we'd move the entire checklist item to the new page, but for now just show the
                // wrapped line without a checkbox
                if (rowIndex !== -1) {
                    // assumes only one cell per row
                    const verticalCenterInPoints = data.cell.y + (data.cell.height / 2);
                    const circleXInPoints = data.cell.x - (.1 * INCHES_TO_POINTS);
                    const circleYInPoints = verticalCenterInPoints;
                    this.doc.setDrawColor('#A9A9A9');
                    this.doc.setLineWidth(2);
                    const circleRadius = 8;
                    this.doc.circle(circleXInPoints, circleYInPoints, circleRadius, 'S');

                    const isComplete = checklist[rowIndex].completed;
                    if (isComplete){
                        // this.doc.setFontSize(20);
                        // this.doc.setTextColor('gray');
                        // this.doc.text('×', circleXInPoints + 0, circleYInPoints + 7, { align: 'center' });
                        const checkSize = 25;
                        this.doc.addImage(
                            '/assets/images/ionic-checkmark.png',
                            circleXInPoints - 10,
                            circleYInPoints - 15,
                            checkSize,
                            checkSize,
                        );
                    }
                }
            },
            theme: 'plain',
            margin: {
                top: 1 * INCHES_TO_POINTS,
                bottom: 1 * INCHES_TO_POINTS,
                left: (this.margins.left + .5) * INCHES_TO_POINTS,
                right: this.margins.right * INCHES_TO_POINTS },
            startY,
            body: [
                ...checklist.map(item => [item.name])
            ],
        });

        this.cursor.y = (this.doc as any).lastAutoTable.finalY / INCHES_TO_POINTS + .5;
    };

    private addChecklists = (params: IMissionReportParams) => {

        // console.log(this.doc.getFont());
        // "WinAnsiEncoding" "helvetica"
        this.cursor.x = this.margins.left;
        this.cursor.y = 1.5;
        this.h1({ text: 'Checklists' });

        this.cursor.x = this.cursor.x + this.indentWidth;
        this.cursor.y = this.cursor.y + .2;

        this.h2({ text: 'Pre Flight' });
        const checklistSections = params.mission.state.checklist?.sections || [];

        const tableMargin = -.15;

        this.addChecklistItems({
            checklist: checklistSections.find(s => s.id === 'pre-flight')?.items || [],
            startY: (this.cursor.y + tableMargin) * INCHES_TO_POINTS,
        });

        this.h2({ text: 'On Site' });

        this.addChecklistItems({
            checklist: checklistSections.find(s => s.id === 'on-site')?.items || [],
            startY: (this.cursor.y + tableMargin) * INCHES_TO_POINTS,
        });

        this.h2({ text: 'Post Flight' });

        this.addChecklistItems({
            checklist: checklistSections.find(s => s.id === 'post-flight')?.items || [],
            startY: (this.cursor.y + tableMargin) * INCHES_TO_POINTS,
        });
    };

    private addNotes = async (params: IMissionReportParams) => {
        this.h1({ text: 'Notes '});

        const notesContainer = document.createElement('div');

        const imageWidthInPDFInches = 7.5;
        const imageWidthInPDFPixels = imageWidthInPDFInches * 96;

        const imageHeightInPDFInches = 8;
        const imageHeightInPDFPixels = imageHeightInPDFInches * 96;

        notesContainer.style.width = `${imageWidthInPDFPixels}px`;
        notesContainer.style.height = `${imageHeightInPDFPixels}px`;
        notesContainer.style.position = 'absolute';
        notesContainer.classList.add('notes-for-pdf');

        // notesContainer.style.left = '0px';
        notesContainer.style.left = '100vw';

        notesContainer.innerHTML = params.mission.state.notesHtml || '';

        document.body.appendChild(notesContainer);

        const canvas = await html2canvas(notesContainer);

        notesContainer.remove();

        this.doc.addImage(
            canvas.toDataURL(),
            'PNG',
            this.cursor.x * INCHES_TO_POINTS,
            this.cursor.y * INCHES_TO_POINTS,
            imageWidthInPDFInches * INCHES_TO_POINTS,
            imageHeightInPDFInches * INCHES_TO_POINTS,
            undefined,
            this.imageCompression
        );
    };

    private addLAANCAuthorizations = (params: IMissionReportParams) => {
        for (const authorization of params.mission.getRelevantAuthorizations()){
            this.doc.addPage();

            this.cursor.x = this.margins.left;
            this.cursor.y = 1.5;

            this.h1({ text: 'LAANC Authorization Details'});
            this.cursor.y = this.cursor.y + .25;

            this.h2({ text: 'LAANC Status'});
            const endTime = params.mission.getEndTime();
            const statusLabel = this.authorizationTextProvider.getAuthorizationStatusLabel({ authorization, endTime });
            this.p({ text: statusLabel });

            const center = params.mission.getLocation();

            const getFormattedTime = (time: string) => formatLocalDateAndTime({ location: center, time, withTimeZone: true });

            this.cursor.y = this.cursor.y + .25;
            this.h2({ text: 'LAANC Details'});
            // TODO: drop font size, this is close to overflowing
            this.p({ text: `APY${authorization.operationID} / ${authorization.airspaceIdent}, ${getFormattedTime(authorization.startTime)} - ${getFormattedTime(authorization.endTime)}, Max Alt ${params.mission.getMaxAltitude()} ft`});

            this.cursor.y = this.cursor.y + .25;
            this.h2({ text: 'LAANC Authorization Statement'});

            const text = this.authorizationTextProvider.getAuthorizationAsText({
                authorization,
                operator: params.operator,
                altitude: params.mission.getMaxAltitude(),
                location: params.mission.getLocation()
            });

            // TODO: font size and padding are different
            autoTable(this.doc, {
                theme: 'plain',
                margin: {
                    top: 1 * INCHES_TO_POINTS,
                    bottom: 1 * INCHES_TO_POINTS,
                    left: (this.margins.left - .0) * INCHES_TO_POINTS,
                    right: (this.margins.right - .0) * INCHES_TO_POINTS,
                },
                columnStyles: {
                    0: {
                        fontSize: this.pPoints,
                        cellPadding: { left: 0, right: 0, top: 10 }
                    }
                },
                startY: (this.cursor.y - .20) * INCHES_TO_POINTS,
                body: [
                    [text]
                ],
            });
        }

    };

    // SLOW, web, 394 KB, 1.5 MB
    private imageCompression: 'SLOW' = 'SLOW';

    private addMap = async (params: IMissionReportParams) => {
        const imageWidthInPDFInches = 7.5;
        const imageWidthInPDFPixels = imageWidthInPDFInches * 96;
        const canvasWidthPixels = imageWidthInPDFPixels;

        const imageHeightInPDFInches = 3.5 - .2; // .1 top and bottom
        const imageHeightInPDFPixels = imageHeightInPDFInches * 96;
        const canvasHeightPixels = imageHeightInPDFPixels;

        const canvas = document.createElement('canvas');
        canvas.width = canvasWidthPixels;
        canvas.height = canvasHeightPixels;

        const temporaryMapContainer = document.createElement('div');
        temporaryMapContainer.style.width = `${imageWidthInPDFPixels}px`;
        temporaryMapContainer.style.height = `${imageHeightInPDFPixels}px`;
        temporaryMapContainer.style.position = 'absolute';

        // switch to this to see the map in the dom
        // temporaryMapContainer.style.left = '0px';
        temporaryMapContainer.style.left = '100vw';

        document.body.appendChild(temporaryMapContainer);

        const map = new mapboxgl.Map({
            container: temporaryMapContainer,
            style: 'mapbox://styles/mapbox/satellite-streets-v12',

            center: params.mission.getLocation(),

            // zoom gets set later
            zoom: 12,
            maxZoom: 15,

            // we need this to be true to get an image from the mapbox canvas
            preserveDrawingBuffer: true,

            dragRotate: false,
            touchPitch: false,
            scrollZoom: false,

            // when attributionControl is true, the logo doesn't make it to the PDF
            // and the other attribution includes "Improve This Map" which doesn't make sense on
            // a non-clickable PDF
            attributionControl: false,
        });

        map.addControl(new mapboxgl.ScaleControl({ unit: 'imperial' }), 'bottom-right');
        const mapControls = temporaryMapContainer.querySelector('.mapboxgl-ctrl-bottom-right') as any;
        mapControls.style.bottom = '10px';

        await new Promise<void>(resolve => {
            map.on('load', () => {
                resolve();
            });
        });

        // remove the mapbox logo
        // to avoid white box in safari when going through html2canvas
        temporaryMapContainer.querySelector('.mapboxgl-ctrl-logo')!.remove();

        const radius = params.mission.getMissionRadius().getMissionRadius() as Feature<Polygon, { color?: string; }>;

        radius.properties.color = this.theme.getColor(this.missionColorProvider.getColor(params.mission.getMissionRadius()));
        new mapboxgl.Marker({ color: radius.properties.color }).setLngLat(params.mission.getLocation()).addTo(map);

        map.addSource('source:radius', {
            'type': 'geojson',
            'data': { type: 'FeatureCollection', features: [radius] },
        });

        map.addLayer({
            'id': 'layer:radius-fill',
            'type': 'fill',
            'source': 'source:radius',
            'layout': {},
            'minzoom': 12,
            'paint': {
                'fill-color': ['get', 'color'],
                'fill-opacity': 0.3
            }
        });

        const [xmin, ymin, xmax, ymax] = turf.bbox(radius);
        const bbox: [[number, number], [number, number]] = [[xmin, ymin], [xmax, ymax]];

        // NOTE: padding 125 (px) cannot exceed half of the map / canvas height px
        //  assuming height is less than width (and therefore the limiting factor)
        map.fitBounds(bbox, { padding: 50, duration: 0 });

        map.addLayer({
            'id': 'layer:radius-outline',
            'type': 'line',
            'source': 'source:radius',
            'minzoom': 12,
            'layout': {},
            'paint': {
                'line-color': '#fff',
                'line-width': [
                    'interpolate',
                    // NOTE: not sure what this 2 is for
                    ['exponential', 2],
                    ['zoom'],
                    // exponentially interpolate from zoom 11 (1 px wide) to zoom 17 (4 px wide)
                    11, 1.5,
                    17, 5

                    // NOTE this makes it stay about 3 ?meters? wide
                    // that isn't what we want... but I kept it cause we might ;)
                    // 10, ['*', 3, ['^', 2, -6]],
                    // 24, ['*', 3, ['^', 2, 8]]
                ]
            }
        });

        // wait for the map to be ready
        const startedAt = Date.now();

        while (true){
            await new Promise<void>(r => setTimeout(r, 100));
            if (map.isStyleLoaded() && map.areTilesLoaded()){
                break;
            }
            else if (Date.now() - startedAt > 1000){
                break;
            }
        }

        // we don't want JUST the mapbox canvas, or we'll miss things like the marker
        const canvasWithMapboxDOM = await html2canvas(temporaryMapContainer);

        map.remove();
        temporaryMapContainer.remove();


        this.doc.addImage(
            canvasWithMapboxDOM.toDataURL(),
            'PNG',
            this.cursor.x * INCHES_TO_POINTS,
            this.cursor.y * INCHES_TO_POINTS,
            imageWidthInPDFInches * INCHES_TO_POINTS,
            imageHeightInPDFInches * INCHES_TO_POINTS,
            undefined,
            this.imageCompression
            // 'FAST' // 3.9 web, 17.8 MB android
            // 'SLOW' // 3.8 MB 17.3 MB
            // 'MEDIUM' // 3.8 MB 17.5 MB
            // 'NONE' // 4.4 MB 21.9 MB
        );

        // https://www.mapbox.com/brand-guidelines
        const widthInches = .5;
        // original 800 x 180 pixels
        const heightInches = 180 / 800 * widthInches;
        this.doc.addImage(
            '/assets/images/mapbox-logo-white.png',
            'PNG',
            (this.cursor.x + .1) * INCHES_TO_POINTS,
            (this.cursor.y + imageHeightInPDFInches - .2) * INCHES_TO_POINTS,
            widthInches * INCHES_TO_POINTS,
            heightInches * INCHES_TO_POINTS,
            undefined,
            this.imageCompression
        );

        this.doc.setFontSize(6);
        this.doc.setTextColor('white');
        this.doc.text(
            '© Mapbox, © OpenStreetMap',
            6.8 * INCHES_TO_POINTS,
            (this.cursor.y + imageHeightInPDFInches - .09) * INCHES_TO_POINTS,
        );

        this.cursor.y = this.cursor.y + imageHeightInPDFInches + .1;;

    };
    private rowMargin = .1;


    private addPilotDetails = (params: IMissionReportParams) => {
        const heightNeeeded = 1.5;
        // uncomment this to see if height Needed reflects the actual bottom of the content
        // this.doc.rect(this.cursor.x * INCHES_TO_POINTS, this.cursor.y * INCHES_TO_POINTS, (this.pageSize.width - this.margins.left - this.margins.right) * INCHES_TO_POINTS, heightNeeeded * INCHES_TO_POINTS);
        const heightRemaining = this.pageSize.height - 1 - this.cursor.y;
        if (heightRemaining < heightNeeeded){
            this.doc.addPage();
            this.cursor.y = 1.5;
        }

        this.h1({ text: 'Pilot Details' });

        this.cursor.x = this.margins.left + this.indentWidth;

        const row1Y = this.cursor.y;


        this.h2({ text: 'Pilot in Command' });
        const name = `${params.operator.firstName || ''} ${params.operator.lastName || ''}`;
        this.p({ text: name.trim() });

        this.cursor.y = this.cursor.y + this.rowMargin;

        const row2Y = this.cursor.y;

        this.h2({ text: 'Contact Number' });
        let phone = params.operator.phone || '';
        if (phone.length === 10){
            phone = `(${phone.slice(0,3)}) ${phone.slice(3,6)}-${phone.slice(6,10)}`;
        }
        this.p({ text: phone });

        this.cursor.y = this.cursor.y + this.rowMargin;

        const row3Y = this.cursor.y;

        this.h2({ text: 'Contact Email' });
        this.p({ text: params.operator.email || '' });

        this.cursor.x = this.pageSize.width / 2;
        this.cursor.y = row1Y;

        this.h2({ text: 'Business Name' });
        this.p({ text: params.operator.businessName || '' });

        this.cursor.y = row2Y;
        let certificateType = 'TRUST / Remote Pilot';
        if (params.operator.certificationNumber && params.operator.certificationType  === 'part-107'){
            certificateType = 'Part 107 Remote Pilot Certificate';
        }
        else if (params.operator.certificationNumber){
            certificateType = 'Recreational (TRUST Certificate)';
        }
        this.h2({ text: certificateType });
        this.p({ text: params.operator.certificationNumber || '' });

        this.cursor.y = row3Y;
        this.h2({ text: `Issued / Renewed Date` });
        this.p({ text: params.operator.certificationDate || '' });

        this.cursor.x = this.margins.left;

    };

    buildPDF = async (params: IMissionReportParams) => {
        var doc = new jsPDF({
            orientation: 'portrait',
            unit: 'pt',// switch to points
            // 76 points to an inch
            format: 'letter'
        });
        this.doc = doc;

        this.cursor.x = .5;
        this.cursor.y = 1.1;

        // let boxCursorY = 0;
        // let height = 1;
        // this.doc.setFillColor('orange');
        // this.doc.rect(0, boxCursorY * INCHES_TO_POINTS, .5 * INCHES_TO_POINTS, (boxCursorY + height) * INCHES_TO_POINTS, 'F');
        // boxCursorY = boxCursorY + height;

        // height = 3.5;
        // this.doc.setFillColor('gray');
        // this.doc.rect(0, boxCursorY * INCHES_TO_POINTS, .5 * INCHES_TO_POINTS, (boxCursorY + height) * INCHES_TO_POINTS, 'F');
        // boxCursorY = boxCursorY + height;

        // height = 2.7;
        // this.doc.setFillColor('orange');
        // this.doc.rect(0, boxCursorY * INCHES_TO_POINTS, .5 * INCHES_TO_POINTS, (boxCursorY + height) * INCHES_TO_POINTS, 'F');
        // boxCursorY = boxCursorY + height;

        // height = 2.6;
        // this.doc.setFillColor('gray');
        // this.doc.rect(0, boxCursorY * INCHES_TO_POINTS, .5 * INCHES_TO_POINTS, (boxCursorY + height) * INCHES_TO_POINTS, 'F');
        // boxCursorY = boxCursorY + height;

        // height = .75;
        // this.doc.setFillColor('orange');
        // this.doc.rect(0, boxCursorY * INCHES_TO_POINTS, .5 * INCHES_TO_POINTS, (boxCursorY + height) * INCHES_TO_POINTS, 'F');
        // boxCursorY = boxCursorY + height;

        await this.addMap(params);

        autoTable(this.doc, {
            theme: 'plain',
            margin: {
                top: 0,
                bottom: 0,
                left: this.margins.left * INCHES_TO_POINTS,
                right: this.margins.right * INCHES_TO_POINTS
            },
            startY: this.cursor.y * INCHES_TO_POINTS,
            bodyStyles: {
                fontSize: 8,
            },
            body: [
                [`Important Notice: This document captures a snapshot of the referenced operation at the time of export. This operation may have been shared with the FAA and does not serve as the sole authorization to fly in the designated area. Airspace conditions are subject to change, and pilots should always check to confirm that the flight remains approved and complies with all FAA sUAS regulations.`]
            ],
        });
        this.cursor.y = (this.doc as any).lastAutoTable.finalY / INCHES_TO_POINTS;

        this.addMissionDetails(params);

        this.addPilotDetails(params);

        this.doc.addPage();

        this.addChecklists(params);

        this.addLAANCAuthorizations(params);

        // notes could be empty, so putting it at the end
        await this.doc.addPage();
        this.cursor.x = this.margins.left;
        this.cursor.y = this.margins.top + 1;
        await this.addNotes(params);


        this.addHeader(params);
        this.addFooter();

        this.doc.setProperties({ title: params.mission.getName() });

        return {
            toDataURIString: () => doc.output('datauristring'),
            toBlobURI: () => doc.output('bloburi').toString(),
            save: () => doc.save(params.mission.getName())
        };
    };
}
